"use client";

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { motion } from "motion/react";

import EmailForm from "./EmailForm/EmailForm";
import VerificationCodeForm from "./VerificationCodeForm/VerificationCodeForm";

import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import LanguageSwitcher from "@/layouts/AuthLayout/components/LanguageSwitcher/LanguageSwitcher.tsx";
import AppLogo from "@/components/Logo.tsx";
import PATHS from "@/routes/paths.ts";

const SignInSteps = () => {
  const { t } = useTranslation("auth");
  const [step, setStep] = useState<0 | 1>(0);
  const stepContent = useMemo(
    () => [
      {
        component: (
          <EmailForm
            goToNextStep={() => {
              setStep(1);
            }}
          />
        ),
      },
      {
        component: <VerificationCodeForm />,
      },
    ],
    [setStep],
  );

  const { component } = stepContent[step];
  const MotionCard = motion(Card);

  return (
    <div className="bg-background flex min-h-dvh items-center justify-center">
      <div className="absolute top-10 right-10 z-50">
        <LanguageSwitcher />
      </div>
      <MotionCard
        layout
        className="bg-foreground/50 flex h-dvh w-screen max-w-xl flex-col items-center justify-center overflow-hidden border-none shadow-2xl backdrop-blur-xl max-md:max-w-screen md:h-[575px] md:w-1/2 md:max-w-2xl md:rounded-2xl"
        layoutId={"form"}
      >
        <CardHeader className="items-center justify-center md:pb-0">
          <CardTitle className="text-center">
            <AppLogo className="h-10" />
          </CardTitle>
        </CardHeader>
        <motion.div
          layout
          animate={{ opacity: 0.05 }}
          className="absolute -top-20 -left-20 h-56 w-56 rounded-full bg-gradient-to-br from-teal-400 to-purple-500"
          initial={{ opacity: 0.05 }}
          layoutId={"blue"}
          transition={{ layout: { duration: 0.75 } }}
        />
        <motion.div
          layout
          animate={{ opacity: 0.05 }}
          className="absolute -right-16 -bottom-16 h-40 w-40 rounded-full bg-gradient-to-tr from-red-400 to-pink-600"
          initial={{ opacity: 0.05 }}
          layoutId={"purple"}
          transition={{ layout: { duration: 0.75 } }}
        />
        <div className="relative">
          <CardContent className="min-w-[75vw] p-2! sm:min-w-[500px] md:py-3">
            {component}
          </CardContent>
          <CardFooter className="flex flex-col justify-end md:pb-0">
            <Link className={"text-accent-foreground text-sm"} to={PATHS.SIGN_UP}>
              {t("signIn.noAccount")}
            </Link>
            <div
              className={
                "text-muted-foreground mt-4 max-w-[90%] text-center text-sm sm:max-w-[75%]"
              }
            >
              <Trans
                components={{
                  link1: (
                    <Link key={"1"} className="text-accent-foreground inline w-fit" to={PATHS.TERMS_OF_SERVICE} />
                  ),
                  link2: (
                    <Link key={"2"} className="text-accent-foreground inline w-fit" to={PATHS.PRIVACY_POLICY} />
                  ),
                }}
                i18nKey={"terms"}
                ns={"auth"}
              />
            </div>
          </CardFooter>
        </div>
      </MotionCard>
    </div>
  );
};

export default SignInSteps;
